import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { RESIDUAL_VALUES, RESIDUAL_VALUE_GRID } from 'features/residualValueGrid/queries';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { Card } from '@totalsoft/rocket-ui';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import WrappedFakeText from 'components/common/WrappedFakeText';
import ResidualValueGridGeneralDataComponent from './ResidualValueGridGeneralDataComponent';
import { useTranslation } from 'react-i18next';
import { required, shape, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import IconButton from 'components/common/buttons/IconButton';
import { useReactiveVar } from '@apollo/client';
import { SAVE_RESIDUAL_VALUE_GRID } from 'features/residualValueGrid/mutations';
import { RESIDUAL_VALUE_GRID_SAVED } from 'features/residualValueGrid/subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_RESIDUAL_VALUE_GRIDS_WRITE } from 'constants/rightsCodes';
import { Prompt } from 'react-router-dom';
import { useMutation } from 'hooks/apollo/useMutation';
import { Portal, Tooltip } from '@mui/material';
import { isAssetLevelResidualValuesVar, isGridEditableVar } from '../variables';
import useQuery from 'hooks/apollo/useQuery';
import { CompactTitle } from 'components/common/Styles';
import useSubscription from 'hooks/apollo/useSubscription';
import { isValidDateFormat } from 'utils/simple-validations';

const validator = shape({
  code: required,
  name: required,
  assetLevelResidualValues: required,
  fromDate: isValidDateFormat |> stopOnFirstFailure,
  toDate: isValidDateFormat |> stopOnFirstFailure
});

const ResidualValueGridGeneralDataContainer = ({ id }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const historyContext = useHistoryWithContext();

  const canWrite = usePermission(ADMIN_RESIDUAL_VALUE_GRIDS_WRITE);
  const isEditable = useReactiveVar(isGridEditableVar);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({ assetLevelResidualValues: false });
  const [validation, validate] = useDirtyFieldValidation(validator);

  const [pending, setPending] = useState(false);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #region Data fetching

  const { loading } = useQuery(RESIDUAL_VALUE_GRID, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens(data.residualValueGrid);
      isGridEditableVar(data.residualValueGrid.isEditable || false);
      isAssetLevelResidualValuesVar(data.residualValueGrid.assetLevelResidualValues || false);
    }
  });

  const { data: residualValuesData } = useQuery(RESIDUAL_VALUES);

  const residualValues = useMemo(() => residualValuesData?.residualValues || [], [residualValuesData]);

  // #endregion

  // #region Save

  const [saveResidualValueGrid] = useMutation(SAVE_RESIDUAL_VALUE_GRID, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    // eslint-disable-next-line no-unused-vars
    const { applicableToName, isEditable, ...sanitizedData } = data;

    setPending(true);

    saveResidualValueGrid({
      variables: { input: sanitizedData }
    });
  };

  useSubscription(RESIDUAL_VALUE_GRID_SAVED, {
    onError: error => addToast(error, 'error'),
    onData: ({ client, data }) => {
      resetLens(data.data.residualValueGridSaved.residualValueGrid);
      setPending(false);
      addToast(t('ResidualValueGrid.Saved'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'residualValueGrids' });
      if (!id) {
        const newId = data.data.residualValueGridSaved.residualValueGrid.id;
        setTimeout(() => {
          historyContext.push(`/residualValueGrids/${newId}`);
        }, 100);
      }
    }
  });

  // #endregion

  const disabled = useMemo(() => !canWrite || (!isEditable && id) || pending, [canWrite, isEditable, id, pending]);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  return (
    <>
      <Prompt when={isEditable && isDirty} message={t('LeavingWithoutSaving')} />
      <Card icon={Grid4x4Icon} actions={<IconButton type='save' onClick={handleSave} disabled={!isDirty || disabled} />}>
        {loading && <WrappedFakeText />}
        {!loading && (
          <ResidualValueGridGeneralDataComponent
            lens={lens}
            residualValues={residualValues}
            validation={validation}
            disabled={disabled}
            isNew={!id}
          />
        )}
      </Card>
      <Portal container={() => document.getElementById('residual-value-grid-title')}>
        <Tooltip title={lens.name |> get}>
          <span>
            <CompactTitle variant='subtitle1' fontWeight='bold'>
              {t('ResidualValueGrid.Title', { name: lens.name |> get })}
            </CompactTitle>
          </span>
        </Tooltip>
      </Portal>
    </>
  );
};

ResidualValueGridGeneralDataContainer.propTypes = {
  id: PropTypes.number
};

export default ResidualValueGridGeneralDataContainer;
